import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Users, Building2, Calendar, TrendingUp } from 'lucide-react';
import { dashboardService } from '../../services';
import exportService from '../../services/exportService';

/**
 * Super Admin Analytics Page
 * Platform-wide analytics dashboard showing metrics across all organizations
 */

const SuperAdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrganizations: 0,
    totalSessions: 0,
    activeSessions: 0,
    totalParticipants: 0,
    avgEngagement: 0,
  });
  const [sessionsOverTime, setSessionsOverTime] = useState([]);
  const [usersByRole, setUsersByRole] = useState([]);
  const [orgsByPlan, setOrgsByPlan] = useState([]);
  const [topOrganizations, setTopOrganizations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  // Heuristic insights derived from the metrics already on this page —
  // engagement health, plan adoption, customer concentration, growth trend etc.
  const [platformInsights, setPlatformInsights] = useState([]);

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  const PLAN_COLORS = {
    free: '#6B7280',
    pro: '#4F46E5',
    pro_plus: '#7C3AED',
    enterprise_starter: '#10B981',
    enterprise_scale: '#F59E0B',
    expired: '#EF4444',
  };

  // Convert time range to days
  const getPeriodDays = (range) => {
    switch (range) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      case 'all': return 365;
      default: return 30;
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const period = getPeriodDays(timeRange);

      // Fetch all analytics data in parallel
      const [statsRes, sessionAnalytics, userAnalytics, orgAnalytics, activityRes] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getSessionAnalytics(period),
        dashboardService.getUserAnalytics(period),
        dashboardService.getOrganizationAnalytics(period),
        dashboardService.getRecentActivity(10),
      ]);

      // Set main stats — use time-filtered data from analytics responses
      if (statsRes.success && statsRes.data) {
        const data = statsRes.data;

        // Use time-filtered counts from analytics endpoints when available
        const filteredUsers = userAnalytics.success && userAnalytics.data?.totalInPeriod !== undefined
          ? userAnalytics.data.totalInPeriod
          : (data.overview?.totalUsers || data.users?.total || 0);

        const filteredSessions = sessionAnalytics.success && sessionAnalytics.data?.totalInPeriod !== undefined
          ? sessionAnalytics.data.totalInPeriod
          : (data.overview?.totalSessions || data.sessions?.total || 0);

        const filteredOrgs = orgAnalytics.success && orgAnalytics.data?.totalInPeriod !== undefined
          ? orgAnalytics.data.totalInPeriod
          : (data.overview?.totalOrganizations || data.organizations?.total || 0);

        const filteredParticipants = sessionAnalytics.success && sessionAnalytics.data?.totalParticipantsInPeriod !== undefined
          ? sessionAnalytics.data.totalParticipantsInPeriod
          : (data.overview?.totalParticipants || 0);

        const filteredEngagement = sessionAnalytics.success && sessionAnalytics.data?.avgEngagementInPeriod !== undefined
          ? sessionAnalytics.data.avgEngagementInPeriod
          : (data.overview?.avgEngagementRate || 0);

        setStats({
          totalUsers: filteredUsers,
          totalOrganizations: filteredOrgs,
          totalSessions: filteredSessions,
          activeSessions: data.sessions?.byStatus?.live || 0,
          totalParticipants: filteredParticipants,
          avgEngagement: filteredEngagement,
        });

        // Set organizations by plan — show all plans even with 0 count.
        // Each plan row counts client orgs + solo trainers (type='individual'
        // orgs) on that plan, so the donut reflects platform-wide tier mix
        // rather than client-orgs-only. Expired bucket sums lapsed accounts
        // from both groups so admins see one combined "needs renewal" number.
        const allPlans = ['free', 'pro', 'pro_plus', 'enterprise_starter', 'enterprise_scale', 'expired'];
        const planLabels = {
          free: 'Free',
          pro: 'Pro',
          pro_plus: 'Pro Plus',
          enterprise_starter: 'Enterprise Starter',
          enterprise_scale: 'Enterprise Scale',
          expired: 'Expired',
        };
        const byPlanData = data.organizations?.byPlan || {};
        const indivBuckets = data.organizations?.individualTrainers?.byPlan || {};
        const planData = allPlans.map(planKey => ({
          name: planLabels[planKey],
          value: (byPlanData[planKey] || 0) + (indivBuckets[planKey] || 0),
          fill: PLAN_COLORS[planKey] || '#6B7280',
        }));
        setOrgsByPlan(planData);
      }

      // Set sessions over time from dailyTrend
      if (sessionAnalytics.success && sessionAnalytics.data?.dailyTrend) {
        const formattedData = sessionAnalytics.data.dailyTrend.map(item => ({
          date: item._id,
          count: item.count,
        }));
        setSessionsOverTime(formattedData);
      }

      // Set users by role
      if (userAnalytics.success && userAnalytics.data?.byRole) {
        const roleData = Object.entries(userAnalytics.data.byRole)
          .filter(([_, value]) => value > 0)
          .map(([name, value]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1).replace('-', ' '),
            value,
          }));
        setUsersByRole(roleData);
      }

      // Set top organizations - combine sessions and users data
      if (orgAnalytics.success && orgAnalytics.data) {
        const topBySessions = orgAnalytics.data.topBySessions || [];
        const topByUsers = orgAnalytics.data.topByUsers || [];

        // Merge the data
        const orgMap = new Map();
        topBySessions.forEach(org => {
          orgMap.set(org._id?.toString(), {
            _id: org._id,
            name: org.organizationName,
            sessionsCount: org.sessionCount,
            usersCount: 0,
          });
        });
        topByUsers.forEach(org => {
          const existing = orgMap.get(org._id?.toString());
          if (existing) {
            existing.usersCount = org.userCount;
          } else {
            orgMap.set(org._id?.toString(), {
              _id: org._id,
              name: org.organizationName,
              sessionsCount: 0,
              usersCount: org.userCount,
            });
          }
        });

        setTopOrganizations(Array.from(orgMap.values()).slice(0, 5));
      }

      // Compute heuristic platform insights from the metrics we just collected.
      // We scope this off the local variables (not state) because setState is
      // async — by the time React commits, fetchAnalyticsData has already
      // returned. This way the insights row stays in lock-step with the rest
      // of the page on every refresh.
      const _stats = {
        totalUsers: statsRes?.success ? (userAnalytics?.data?.totalInPeriod ?? statsRes.data?.overview?.totalUsers ?? 0) : 0,
        totalOrganizations: statsRes?.success ? (orgAnalytics?.data?.totalInPeriod ?? statsRes.data?.overview?.totalOrganizations ?? 0) : 0,
        totalSessions: statsRes?.success ? (sessionAnalytics?.data?.totalInPeriod ?? statsRes.data?.overview?.totalSessions ?? 0) : 0,
        // Time-filtered session count, or null if the analytics endpoint
        // didn't supply one. Used by the concentration insight so it never
        // mixes a time-filtered top-org count with an all-time platform total.
        totalSessionsInPeriod: sessionAnalytics?.success && typeof sessionAnalytics?.data?.totalInPeriod === 'number'
          ? sessionAnalytics.data.totalInPeriod
          : null,
        activeSessions: statsRes?.data?.sessions?.byStatus?.live || 0,
        avgEngagement: sessionAnalytics?.data?.avgEngagementInPeriod ?? statsRes?.data?.overview?.avgEngagementRate ?? 0,
      };
      const _byPlan = statsRes?.data?.organizations?.byPlan || {};
      const _topBySessions = orgAnalytics?.data?.topBySessions || [];
      const _dailyTrend = sessionAnalytics?.data?.dailyTrend || [];
      setPlatformInsights(generatePlatformInsights(_stats, _byPlan, _topBySessions, _dailyTrend));

      // Set recent activity
      if (activityRes.success && activityRes.data?.activities) {
        const formattedActivities = activityRes.data.activities.map(activity => {
          let title = '';
          let description = '';
          let category = '';
          if (activity.type === 'organization') {
            title = `${activity.data?.name || 'Organization'} was registered`;
            description = `New organization signed up for ${activity.data?.plan || 'free'} plan`;
            category = 'Organizations';
          } else if (activity.type === 'user') {
            const name = `${activity.data?.firstName || ''} ${activity.data?.lastName || ''}`.trim();
            title = `${name || 'User'} joined the platform`;
            description = activity.data?.organization?.name ? `Added to ${activity.data.organization.name}` : 'Registered as new user';
            category = 'Users';
          } else if (activity.type === 'session') {
            title = `${activity.data?.name || 'Session'} was ${activity.action || 'created'}`;
            description = activity.data?.organization?.name ? `In ${activity.data.organization.name}` : '';
            category = 'Sessions';
          }
          return {
            ...activity,
            title,
            description,
            category,
          };
        });
        setRecentActivity(formattedActivities);
      }

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // ─── Platform Insights generator ────────────────────────────────────────
  // Rules read directly off the metrics shown on this page: stats overview,
  // org-by-plan distribution, top-by-sessions concentration, and daily trend.
  // Returns up to 5 insights, each {type, title, description}.
  function generatePlatformInsights(s, byPlan, topBySessions, dailyTrend) {
    const out = [];

    // 1. Empty-platform short-circuit
    if (s.totalSessions === 0 && s.totalOrganizations === 0) {
      out.push({
        type: 'info',
        title: 'No platform activity in range',
        description: 'No organizations or sessions in the selected period. Widen the time range to see historical activity.',
      });
      return out;
    }

    // 2. Engagement health
    const engagementPct = Math.round(s.avgEngagement || 0);
    if (s.avgEngagement >= 80) {
      out.push({ type: 'success', title: 'Strong platform engagement', description: `Average engagement is ${engagementPct}% across all sessions in this period.` });
    } else if (s.avgEngagement >= 60) {
      out.push({ type: 'info', title: 'Healthy engagement', description: `Platform engagement averages ${engagementPct}%. Look at low-performing orgs in the Top Organizations table.` });
    } else if (s.avgEngagement > 0) {
      out.push({ type: 'warning', title: 'Low platform engagement', description: `Platform engagement is only ${engagementPct}%. Sessions may not be fully launched or activity catalogues may be too short.` });
    }

    // 3. Plan adoption — % on paid plans vs free
    const totalOrgs = Object.values(byPlan).reduce((a, b) => a + (b || 0), 0);
    if (totalOrgs > 0) {
      const free = byPlan.free || 0;
      const paid = totalOrgs - free;
      const paidPct = Math.round((paid / totalOrgs) * 100);
      if (paidPct >= 50) {
        out.push({ type: 'success', title: 'Healthy paid mix', description: `${paidPct}% of organizations are on paid plans (${paid}/${totalOrgs}).` });
      } else if (paidPct >= 20) {
        out.push({ type: 'info', title: 'Conversion opportunity', description: `${paidPct}% paid (${paid}/${totalOrgs}). The remaining ${free} free orgs are conversion candidates.` });
      } else {
        out.push({ type: 'warning', title: 'Low paid conversion', description: `Only ${paidPct}% of organizations are paying (${paid}/${totalOrgs}). Most of the platform is on free plans.` });
      }
    }

    // 4. Customer concentration — single org's share of platform sessions.
    // Only runs when both numerator (top org's session count for the selected
    // period) and denominator (platform-wide session total for the same
    // period) are real time-filtered values. If sessionAnalytics didn't
    // return totalInPeriod, we'd be dividing a period count by an all-time
    // total — skip rather than show a misleading percentage.
    if (
      topBySessions.length > 0 &&
      typeof s.totalSessionsInPeriod === 'number' &&
      s.totalSessionsInPeriod > 0
    ) {
      const top = topBySessions[0];
      const topShare = Math.round((top.sessionCount / s.totalSessionsInPeriod) * 100);
      if (topShare >= 30) {
        out.push({
          type: 'warning',
          title: 'Customer concentration risk',
          description: `${top.organizationName} accounts for ${topShare}% of platform sessions (${top.sessionCount}/${s.totalSessionsInPeriod}).`,
        });
      }
    }

    // 5. Growth trend — last 7 days vs prior 7 days from dailyTrend
    if (dailyTrend.length >= 8) {
      const sorted = [...dailyTrend].sort((a, b) => String(a._id).localeCompare(String(b._id)));
      const recent = sorted.slice(-7).reduce((a, b) => a + (b.count || 0), 0);
      const prior = sorted.slice(-14, -7).reduce((a, b) => a + (b.count || 0), 0);
      if (prior > 0) {
        const delta = Math.round(((recent - prior) / prior) * 100);
        if (delta >= 25) {
          out.push({ type: 'success', title: 'Session volume accelerating', description: `Sessions are up ${delta}% week-over-week (${recent} vs ${prior}).` });
        } else if (delta <= -25) {
          out.push({ type: 'warning', title: 'Session volume slowing', description: `Sessions are down ${Math.abs(delta)}% week-over-week (${recent} vs ${prior}).` });
        }
      } else if (recent > 0) {
        out.push({ type: 'info', title: 'New activity emerging', description: `${recent} sessions in the last 7 days after no activity prior.` });
      }
    }

    // 6. Live concurrency
    if (s.activeSessions >= 5) {
      out.push({ type: 'info', title: 'High live concurrency', description: `${s.activeSessions} sessions currently live. Monitor server load on the dashboard.` });
    }

    if (out.length === 0) {
      out.push({ type: 'info', title: 'Platform looks healthy', description: 'No anomalies detected in engagement, plan mix, or session volume for this period.' });
    }

    return out.slice(0, 5);
  }

  const getInsightIcon = (type) => {
    const icons = {
      success: (
        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      info: (
        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
    };
    return icons[type] || icons.info;
  };

  const getInsightBg = (type) => ({
    success: 'bg-green-50 border-green-200',
    warning: 'bg-orange-50 border-orange-200',
    info: 'bg-blue-50 border-blue-200',
  }[type] || 'bg-blue-50 border-blue-200');

  // Helper to get relative time
  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const periodDays = getPeriodDays(timeRange);
      await exportService.exportAnalyticsCSV(periodDays);
      toast.success('Analytics exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export analytics');
    } finally {
      setIsExporting(false);
    }
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-lg px-3.5 py-2.5">
          {label && <p className="text-xs text-neutral-500 mb-1">{typeof formatter === 'function' ? formatter(label) : label}</p>}
          {payload.map((entry, i) => (
            <p key={i} className="text-sm font-semibold text-neutral-900">
              {entry.value} {entry.name || ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 space-y-5 bg-neutral-50/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-neutral-900 transition-colors mb-3 group"
            >
              <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl sm:text-xl sm:text-2xl font-bold text-neutral-900">Platform Analytics</h1>
            <p className="text-sm text-neutral-500 mt-0.5">Monitor platform-wide metrics and trends</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export
                </>
              )}
            </Button>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-xl hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer appearance-none pr-9"
              style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Stat Cards - 4 primary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Users"     value={stats.totalUsers.toLocaleString()} icon={Users}      variant="blue" />
              <StatCard label="Organizations"   value={stats.totalOrganizations}           icon={Building2}  variant="violet" />
              <StatCard label="Total Sessions"  value={stats.totalSessions}                icon={Calendar}   variant="emerald" />
              <StatCard label="Avg Engagement"  value={`${Math.round(stats.avgEngagement || 0)}%`} icon={TrendingUp} variant="amber" />
            </div>

            {/* Row 2: Sessions Overview + Users by Role */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Sessions Overview - Area Chart */}
              <div className="lg:col-span-7 bg-white rounded-2xl border border-neutral-100 p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-base font-bold text-neutral-900">Sessions Overview</h2>
                    <p className="text-xs text-neutral-500 mt-0.5">Sessions created over time</p>
                  </div>
                </div>
                <div className="h-72">
                  {sessionsOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sessionsOverTime}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={true} vertical={false} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatDate}
                          tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#9CA3AF' }}
                          axisLine={false}
                          tickLine={false}
                          width={35}
                        />
                        <Tooltip
                          labelFormatter={formatDate}
                          contentStyle={{
                            borderRadius: '12px',
                            border: '1px solid #F3F4F6',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            padding: '10px 14px',
                          }}
                          formatter={(value) => [`${value} sessions`, 'Count']}
                        />
                        <Line
                          type="natural"
                          dataKey="count"
                          stroke="#6366F1"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 6, stroke: '#6366F1', strokeWidth: 2, fill: '#fff' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                      No session data available
                    </div>
                  )}
                </div>
              </div>

              {/* Users by Role - Bar Chart */}
              <div className="lg:col-span-5 bg-white rounded-2xl border border-neutral-100 p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-base font-bold text-neutral-900">Users by Role</h2>
                    <p className="text-xs text-neutral-500 mt-0.5">{stats.totalUsers.toLocaleString()} total</p>
                  </div>
                </div>
                <div className="h-72">
                  {usersByRole.length > 0 ? (
                    <div className="flex flex-col h-full">
                      <div className="flex-1 min-h-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={usersByRole} barCategoryGap="25%">
                            <XAxis
                              dataKey="name"
                              tick={{ fontSize: 11, fill: '#9CA3AF' }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis hide />
                            <Tooltip
                              contentStyle={{
                                borderRadius: '12px',
                                border: '1px solid #F3F4F6',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                padding: '8px 12px',
                              }}
                              formatter={(value, name) => [`${value} users`, name]}
                              cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                            />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={44}>
                              {usersByRole.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Legend */}
                      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-3 border-t border-neutral-100 mt-2">
                        {usersByRole.map((entry, index) => {
                          const total = usersByRole.reduce((sum, e) => sum + e.value, 0);
                          const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                          return (
                            <div key={entry.name} className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                              <span className="text-xs text-neutral-600">{entry.name}</span>
                              <span className="text-xs text-neutral-400">{pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
                      No user data available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Row 3: Plan Distribution + Top Organizations */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
              {/* Organizations by Plan - Donut Chart */}
              <div className="lg:col-span-5 bg-white rounded-2xl border border-neutral-100 p-5">
                <div className="mb-5">
                  <h2 className="text-base font-bold text-neutral-900">Plan Distribution</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">Organizations by subscription</p>
                </div>
                {orgsByPlan.length > 0 ? (
                  <div className="flex flex-col">
                    <div className="relative" style={{ height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={orgsByPlan}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                            stroke="none"
                          >
                            {orgsByPlan.map((entry) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid #F3F4F6',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                              padding: '8px 12px',
                            }}
                            formatter={(value, name) => [`${value} orgs`, name]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl sm:text-2xl font-bold text-neutral-900">{orgsByPlan.reduce((sum, p) => sum + p.value, 0)}</span>
                        <span className="text-xs text-neutral-500">Total</span>
                      </div>
                    </div>
                    {/* Legend with values */}
                    <div className="space-y-2 pt-4 border-t border-neutral-100 mt-3">
                      {orgsByPlan.map((plan) => {
                        const total = orgsByPlan.reduce((sum, p) => sum + p.value, 0);
                        const pct = total > 0 ? Math.round((plan.value / total) * 100) : 0;
                        return (
                          <div key={plan.name} className="flex items-center justify-between px-1 py-1 rounded-lg hover:bg-neutral-50 transition-colors">
                            <div className="flex items-center gap-2.5">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: plan.fill }} />
                              <span className="text-sm text-neutral-600">{plan.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-neutral-900">{plan.value}</span>
                              <span className="text-xs text-neutral-400">{pct}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-neutral-400 text-sm">
                    No plan data available
                  </div>
                )}
              </div>

              {/* Top Organizations - Table */}
              <div className="lg:col-span-7 bg-white rounded-2xl border border-neutral-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base font-bold text-neutral-900">Top Organizations</h2>
                    <p className="text-xs text-neutral-500 mt-0.5">By sessions & users</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/organizations')}>
                    See All
                  </Button>
                </div>
                {topOrganizations.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead>
                        <tr className="border-b border-neutral-100">
                          <th className="text-left py-2.5 px-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Organization</th>
                          <th className="text-left py-2.5 px-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Sessions</th>
                          <th className="text-right py-2.5 px-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topOrganizations.map((org, index) => (
                          <tr
                            key={org._id || index}
                            className="border-b border-neutral-50 hover:bg-neutral-50/50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/admin/organizations/${org._id}`)}
                          >
                            <td className="py-3.5 px-2">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 font-semibold text-xs">
                                  {index + 1}
                                </div>
                                <span className="text-sm font-medium text-neutral-900">{org.name}</span>
                              </div>
                            </td>
                            <td className="py-3.5 px-2">
                              <div className="flex items-center gap-2.5">
                                <div className="w-20 bg-neutral-100 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-1.5 rounded-full bg-indigo-500 transition-all duration-500"
                                    style={{ width: `${Math.min(100, (org.sessionsCount / (topOrganizations[0]?.sessionsCount || 1)) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-sm text-neutral-700 font-medium">{org.sessionsCount || 0}</span>
                              </div>
                            </td>
                            <td className="py-3.5 px-2 text-right">
                              <Badge variant="primary" size="sm">{org.usersCount || 0}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-neutral-400 text-sm">
                    No organization data available
                  </div>
                )}
              </div>
            </div>

            {/* AI Insights - Platform-wide heuristics derived from the metrics above */}
            <div className="bg-white rounded-2xl border border-neutral-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-bold text-neutral-900">AI Insights</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">Derived from platform metrics in this period</p>
                </div>
              </div>
              {platformInsights.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {platformInsights.map((insight, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex items-start gap-2 p-3 rounded-xl border ${getInsightBg(insight.type)}`}
                    >
                      {getInsightIcon(insight.type)}
                      <div>
                        <p className="text-xs font-semibold text-neutral-900">{insight.title}</p>
                        <p className="text-xs text-neutral-600 mt-0.5">{insight.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-xs text-neutral-500">More activity is needed to surface platform insights.</p>
                </div>
              )}
            </div>

            {/* Recent Activity - Timeline */}
            <div className="bg-white rounded-2xl border border-neutral-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-base font-bold text-neutral-900">Recent Activity</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">Latest platform events</p>
                </div>
              </div>
              {recentActivity.length > 0 ? (
                <div className="relative">
                  {recentActivity.slice(0, 8).map((activity, index) => {
                    const isLast = index === Math.min(recentActivity.length, 8) - 1;
                    const iconConfig = {
                      organization: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
                      user: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' },
                      session: { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                    };
                    const config = iconConfig[activity.type] || iconConfig.session;
                    return (
                      <div key={activity._id || index} className="relative flex gap-4 pb-6 last:pb-0">
                        {/* Timeline line */}
                        {!isLast && (
                          <div className="absolute left-[17px] top-10 bottom-0 w-px bg-neutral-200" />
                        )}
                        {/* Icon */}
                        <div className={`relative z-10 w-9 h-9 rounded-full ${config.bg} ${config.text} flex items-center justify-center flex-shrink-0`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.icon} />
                          </svg>
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <p className="text-sm text-neutral-900 font-medium">{activity.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-neutral-400">{getTimeAgo(activity.date)}</span>
                            {activity.category && (
                              <>
                                <span className="text-neutral-300">·</span>
                                <span className="text-xs font-medium text-neutral-500">{activity.category}</span>
                              </>
                            )}
                          </div>
                          {activity.description && (
                            <div className="mt-2 px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-100">
                              <p className="text-xs text-neutral-600 line-clamp-2">{activity.description}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-neutral-400 text-sm">
                  No recent activity
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminAnalyticsPage;
